// TODO: Refactor this file to use server components, and micro components where applicable

import { useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Avatar,
  AvatarFallback,
  Paragraph,
  CustomerStatCard,
} from '@/components/atoms';
import {
  DataTable,
  PageHeader,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/molecules';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/atoms';
import type { ColumnDef } from '@tanstack/react-table';
import {
  SearchIcon,
  DownloadIcon,
  FilterIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ClipboardList,
  Info,
} from 'lucide-react';
import { cn } from '@/lib/utils';

// Customer data type
interface Customer {
  id: string;
  name: string;
  initials: string;
  contact: 'Whatsapp' | 'Facebook' | 'Instagram';
  lastOrder: string;
  totalSpent: string;
  leadStatus: 'Hot' | 'Warm' | 'Cold';
  avatarColor: string;
}

// Contact data for sharing
interface Contact {
  id: string;
  name: string;
  initials: string;
  channel: 'whatsapp' | 'instagram' | 'facebook';
  lastMessage: string;
  time: string;
}

// Mock contact data
const contactData: Contact[] = [
  {
    id: '1',
    name: 'Chris Friedkly',
    initials: 'CF',
    channel: 'whatsapp',
    lastMessage: 'I need a body oil how much?',
    time: '2 min ago',
  },
  {
    id: '2',
    name: 'Maggie Johnson',
    initials: 'MJ',
    channel: 'whatsapp',
    lastMessage: 'I need a Nike shoe size 45.',
    time: '5 min ago',
  },
  {
    id: '3',
    name: 'Gael Harry',
    initials: 'GH',
    channel: 'instagram',
    lastMessage: 'New York Finest Fruits',
    time: '1 hour ago',
  },
  {
    id: '4',
    name: 'Jenna Sullivan',
    initials: 'JS',
    channel: 'facebook',
    lastMessage: 'Walmart',
    time: '2 hours ago',
  },
  {
    id: '5',
    name: 'Alice Smith',
    initials: 'AS',
    channel: 'whatsapp',
    lastMessage: 'Thanks for the quick response!',
    time: '30 min ago',
  },
  {
    id: '6',
    name: 'Bob Wilson',
    initials: 'BW',
    channel: 'instagram',
    lastMessage: 'I will think about it',
    time: '3 hours ago',
  },
];

// Mock customer data
const customersData: Customer[] = [
  {
    id: '1',
    name: 'Sandra Mary',
    initials: 'SM',
    contact: 'Whatsapp',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Hot',
    avatarColor: 'bg-green-500',
  },
  {
    id: '2',
    name: 'Grace Peace',
    initials: 'GP',
    contact: 'Facebook',
    lastOrder: '2 days ago',
    totalSpent: '₦32,500',
    leadStatus: 'Warm',
    avatarColor: 'bg-blue-600',
  },
  {
    id: '3',
    name: 'Chris Eze',
    initials: 'CE',
    contact: 'Instagram',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Cold',
    avatarColor: 'bg-purple-600',
  },
  {
    id: '4',
    name: 'Sandra Mary',
    initials: 'SM',
    contact: 'Whatsapp',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Hot',
    avatarColor: 'bg-green-500',
  },
  {
    id: '5',
    name: 'Sandra Mary',
    initials: 'SM',
    contact: 'Whatsapp',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Hot',
    avatarColor: 'bg-green-500',
  },
  {
    id: '6',
    name: 'Sandra Mary',
    initials: 'SM',
    contact: 'Whatsapp',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Hot',
    avatarColor: 'bg-green-500',
  },
  {
    id: '7',
    name: 'Sandra Mary',
    initials: 'SM',
    contact: 'Whatsapp',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Hot',
    avatarColor: 'bg-green-500',
  },
  {
    id: '8',
    name: 'Sandra Mary',
    initials: 'SM',
    contact: 'Whatsapp',
    lastOrder: '2 days ago',
    totalSpent: '₦12,500',
    leadStatus: 'Hot',
    avatarColor: 'bg-green-500',
  },
];

export const CustomersPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [showReceipt, setShowReceipt] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [activeContactTab, setActiveContactTab] = useState('all');

  // For demo purposes, set this to false to show the table
  const [showEmptyState] = useState(false);

  const handleRowClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowReceipt(true);
  };

  const handleCloseReceipt = () => {
    setShowReceipt(false);
    setSelectedCustomer(null);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
    setContactSearchQuery('');
    setActiveContactTab('all');
  };

  const handleContactSelect = (contact: Contact) => {
    // Simulate sharing receipt to contact
    alert(`Receipt shared to ${contact.name} via ${contact.channel}!`);
    setShowShareModal(false);
    setContactSearchQuery('');
    setActiveContactTab('all');
  };

  // Filter data based on search query
  const filteredData = customersData.filter(
    (customer) =>
      customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.contact.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter contacts based on search query and active tab
  const filteredContacts = contactData.filter((contact) => {
    const matchesSearch =
      contact.name.toLowerCase().includes(contactSearchQuery.toLowerCase()) ||
      contact.lastMessage
        .toLowerCase()
        .includes(contactSearchQuery.toLowerCase());
    const matchesTab =
      activeContactTab === 'all' || contact.channel === activeContactTab;
    return matchesSearch && matchesTab;
  });

  // Define columns
  const columns: ColumnDef<Customer>[] = [
    {
      accessorKey: 'name',
      header: 'Customer',
      cell: ({ row }) => {
        const customer = row.original;
        return (
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarFallback
                className={cn('text-white font-semibold', customer.avatarColor)}
              >
                {customer.initials}
              </AvatarFallback>
            </Avatar>
            <span className="font-medium">{customer.name}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'contact',
      header: 'Contact',
      cell: ({ row }) => {
        const contact = row.getValue('contact') as string;
        const getContactColor = (channel: string) => {
          switch (channel) {
            case 'Whatsapp':
              return 'text-green-600';
            case 'Facebook':
              return 'text-blue-600';
            case 'Instagram':
              return 'text-purple-600';
            default:
              return 'text-gray-600';
          }
        };
        const getDotColor = (channel: string) => {
          switch (channel) {
            case 'Whatsapp':
              return 'bg-green-600';
            case 'Facebook':
              return 'bg-blue-600';
            case 'Instagram':
              return 'bg-purple-600';
            default:
              return 'bg-gray-600';
          }
        };
        return (
          <div className="flex items-center gap-2">
            <div className={cn('w-2 h-2 rounded-full', getDotColor(contact))} />
            <span className={getContactColor(contact)}>{contact}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'lastOrder',
      header: 'Last Order',
      cell: ({ row }) => (
        <div className="text-sm text-gray-700">{row.getValue('lastOrder')}</div>
      ),
    },
    {
      accessorKey: 'totalSpent',
      header: 'Total Spent',
      cell: ({ row }) => (
        <div className="font-semibold text-gray-900">
          {row.getValue('totalSpent')}
        </div>
      ),
    },
    {
      accessorKey: 'leadStatus',
      header: 'Lead Status',
      cell: ({ row }) => {
        const status = row.getValue('leadStatus') as string;
        const getStatusStyle = (status: string) => {
          switch (status) {
            case 'Hot':
              return 'text-red-600 font-semibold';
            case 'Warm':
              return 'text-yellow-600 font-semibold';
            case 'Cold':
              return 'text-blue-400 font-semibold';
            default:
              return 'text-gray-600';
          }
        };
        return (
          <span className={getStatusStyle(status)}>
            {status} {status === 'Hot' && '🔥'}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row }) => (
        <Button
          variant="link"
          size="sm"
          onClick={() => handleRowClick(row.original)}
          className="text-green-600 hover:text-green-700 p-0"
        >
          View Profile
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        heading="Customers"
        description="Manage your customers and view their details"
      />

      {/* Customer Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <CustomerStatCard
          variant="total"
          title="Total Customers"
          value="100"
          trendValue="57%"
        />
        <CustomerStatCard
          variant="hot"
          title="Hot Customers"
          value="30"
          trendValue="20%"
        />
        <CustomerStatCard
          variant="warm"
          title="Warm Customers"
          value="10"
          trendValue="15%"
        />
        <CustomerStatCard
          variant="cold"
          title="Cold Customers"
          value="60"
          trendValue="35%"
        />
      </div>

      {/* Search and Actions Bar */}
      <div className="flex justify-between items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <FilterIcon className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="outline" size="sm">
            <DownloadIcon className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Sales Table or Empty State */}
      {showEmptyState ? (
        <div className="w-full h-[82vh] flex flex-col items-center justify-center py-20 bg-white rounded-xl">
          <div className="relative mb-6">
            <ClipboardList className="w-16 h-16 text-gray-300" />
            <Info className="w-4 h-4 text-gray-300 absolute -top-1 -right-1" />
          </div>
          <p className="text-gray-500 text-lg">
            You don't have any recent sales
          </p>
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredData}
          onRowClick={handleRowClick}
        />
      )}

      {/* Pagination */}
      {!showEmptyState && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm">
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
          <Button size="sm" className="bg-green-600 text-white">
            1
          </Button>
          <Button variant="outline" size="sm">
            <ArrowLeftIcon className="w-4 h-4 rotate-180" />
          </Button>
        </div>
      )}

      {/* Customer Summary Sheet */}
      <Sheet open={showReceipt} onOpenChange={setShowReceipt}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader className="px-6">
            <div className="flex items-center mb-4">
              <Button variant="ghost" size="icon" onClick={handleCloseReceipt}>
                <ArrowLeftIcon className="w-5 h-5" />
              </Button>
            </div>
            <SheetTitle className="text-center text-2xl font-bold">
              Customer Summary
            </SheetTitle>
          </SheetHeader>

          {selectedCustomer && (
            <div className="mt-8 px-6 space-y-6">
              {/* Customer Avatar with Badge */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <Avatar className="w-32 h-32">
                    <AvatarFallback
                      className={cn(
                        'text-white text-4xl font-bold',
                        selectedCustomer.avatarColor
                      )}
                    >
                      {selectedCustomer.initials}
                    </AvatarFallback>
                  </Avatar>
                  {selectedCustomer.leadStatus === 'Hot' && (
                    <div className="absolute top-0 -right-2 bg-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                      Hot 🔥
                    </div>
                  )}
                  {selectedCustomer.leadStatus === 'Warm' && (
                    <div className="absolute top-0 -right-2 bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Warm
                    </div>
                  )}
                  {selectedCustomer.leadStatus === 'Cold' && (
                    <div className="absolute top-0 -right-2 bg-blue-400 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      Cold
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Information */}
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Customer name:</span>
                  <span className="font-medium text-gray-900">
                    {selectedCustomer.name}
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">WhatsApp number:</span>
                  <span className="font-medium text-gray-900">
                    +2348145499167
                  </span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Location</span>
                  <span className="font-medium text-gray-900">
                    Lagos Nigeria
                  </span>
                </div>
              </div>

              {/* Order History */}
              <div className="mt-8">
                <h3 className="text-green-600 font-semibold text-lg mb-4">
                  Order History
                </h3>

                {/* Order History Table */}
                <div className="space-y-4">
                  {/* Table Header */}
                  <div className="grid grid-cols-5 gap-2 text-sm font-medium text-gray-700 pb-2 border-b">
                    <div>Order ID</div>
                    <div>Date</div>
                    <div>Product(s)</div>
                    <div>Status</div>
                    <div className="text-right">Amount</div>
                  </div>

                  {/* Table Rows */}
                  <div className="grid grid-cols-5 gap-2 text-sm py-3">
                    <div className="text-gray-900">#ORD-2749</div>
                    <div className="text-gray-600">Jun 20</div>
                    <div className="text-gray-900">1x Body Oil</div>
                    <div className="text-green-600 font-medium">Completed</div>
                    <div className="text-right text-gray-900">₦4,500</div>
                  </div>

                  <div className="grid grid-cols-5 gap-2 text-sm py-3">
                    <div className="text-gray-900">#ORD-2694</div>
                    <div className="text-gray-600">Jun 12</div>
                    <div className="text-gray-900">2x Scrub</div>
                    <div className="text-blue-600 font-medium">Paid</div>
                    <div className="text-right text-gray-900">₦10,500</div>
                  </div>
                </div>

                {/* View All Orders Link */}
                <div className="flex justify-center mt-4">
                  <Button
                    variant="ghost"
                    className="text-gray-700 hover:text-gray-900"
                  >
                    View all orders
                    <svg
                      className="w-4 h-4 ml-2"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </Button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mt-8">
                <Button className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-base">
                  Download Profile
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-green-600 text-green-600 hover:bg-green-50 py-6 text-base"
                >
                  Chat Customer
                </Button>
              </div>

              {/* Footer */}
              <div className="mt-8 pt-6 pb-6 text-center">
                <Paragraph className="text-xs text-center tracking-widest text-muted-foreground">
                  POWERED BY GOTHRIVE TECHNOLOGIES
                </Paragraph>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Share Receipt Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-lg w-full max-h-[80vh] flex flex-col">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseShareModal}
              >
                <ArrowLeftIcon className="w-4 h-4" />
              </Button>
              <DialogTitle>Share Receipt</DialogTitle>
              <span></span>
            </div>
          </DialogHeader>

          <div className="flex flex-col flex-1 min-h-0">
            {/* Search Bar */}
            <div className="p-4 flex justify-between items-center">
              <div className="relative flex-1 max-w-md">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search chat"
                  value={contactSearchQuery}
                  onChange={(e) => setContactSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Channel Tabs */}
            <div className="p-4 border-b border-gray-200">
              <Tabs
                value={activeContactTab}
                onValueChange={setActiveContactTab}
              >
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="all">All</TabsTrigger>
                  <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
                  <TabsTrigger value="instagram">Instagram</TabsTrigger>
                  <TabsTrigger value="facebook">Facebook</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {/* Contact List */}
            <div className="flex-1 overflow-y-auto">
              {filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  className="p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleContactSelect(contact)}
                >
                  <div className="flex items-start gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-gray-200 text-gray-600">
                        {contact.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium text-gray-900 truncate">
                          {contact.name}
                        </h3>
                        <span className="text-xs text-gray-500">
                          {contact.time}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 truncate mt-1">
                        {contact.lastMessage}
                      </p>
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500 capitalize">
                            {contact.channel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* All Customers Button */}
            <div className="p-4 border-t border-gray-200">
              <Button
                variant="ghost"
                className="w-full flex items-center justify-center gap-2 text-gray-600"
                onClick={() => {
                  alert('Opening all customers...');
                  setShowShareModal(false);
                }}
              >
                All customers
                <ArrowRightIcon className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
